import Vue from 'vue'
import Vuex from 'vuex'
import Cookies from 'js-cookie';
import { getDeviceList, setDeviceList, clearDeviceList, clearUserToken, clearTokenRenewal } from "@/storage/memoryStorage";
import format from "date-fns/format";
import start_of_today from "date-fns/start_of_today";
import end_of_today from "date-fns/end_of_today";

import { startOfYear, endOfYear, subDays } from "date-fns";

import config from "@/config/config";

Vue.use(Vuex);

const APPLIANCES_GROUP_KEY = 'APPLIANCES_GROUP';
const HIERRARCHY_KEY = 'HIERARCHY';
const DATE_RANGE_KEY = "dateRange";
const SELECTED_DEVICE_KEY = 'selectedDevice';
const PROFILE_KEY = 'userProfile';
const AUTH_KEY = 'AUTH'
const SETTINGS_KEY = 'SETTINGS';
const DATE_RANGE_SELECTORS_KEY = 'DATE_RANGE_SELECTORS';
const APPLICATIONS_KEY = 'APPS';
const EXPIRATION_GENERAL = 30; // days



function initialAuth() {
  try {
    return JSON.parse(Cookies.get(AUTH_KEY));
  } catch (err) {
    return null;
  }
}

function initialDateRange() {
  let dateRangeReturn = {};
  try {
    const dateRangeCookies = JSON.parse(Cookies.get(DATE_RANGE_KEY));
    dateRangeReturn.human = dateRangeCookies.human;
    dateRangeReturn.from = dateRangeCookies.from;
    dateRangeReturn.to = dateRangeCookies.to;
  } catch (err) {
    //default
    dateRangeReturn.human = "today";
    dateRangeReturn.from = format(start_of_today(), "X");
    dateRangeReturn.to = format(end_of_today(), "X");
  }
  return dateRangeReturn;
}

function initialDevice() {
  try {
    return JSON.parse(Cookies.get(SELECTED_DEVICE_KEY));
  } catch (err) {
    return null;
  }
}

function initialDeviceList() {
  try {
    return getDeviceList();
  } catch (err) {
    return null;
  }
}

function initialSettings() {

  //DEFAULT SETTINGS
  const defaultSettings = {
    localization: "en",
    sideBarMax: false,
    phase: {
      "L1": true,
      "L2": true,
      "L3": true,
      "SUM": false,
      "AVG": false
    },
    yAxisType: "linear",
    yearDateRange: {
      from: format(startOfYear(new Date()), "X"),
      to: format(endOfYear(new Date()), "X")
    },
    statsDate: {
      selector: "today",
      from: format(start_of_today(), "X"),
      to: format(end_of_today(), "X")
    },
    graphs: ["3", "2"]
  }

  try {
    const cookiesLoaded = JSON.parse(Cookies.get(SETTINGS_KEY));
    if (!cookiesLoaded.hasOwnProperty("localization")
      || !cookiesLoaded.hasOwnProperty("sideBarMax")
      || !cookiesLoaded.hasOwnProperty("phase")
      || !cookiesLoaded.hasOwnProperty("yAxisType")
      || !cookiesLoaded.hasOwnProperty("yearDateRange")
      || !cookiesLoaded.hasOwnProperty("statsDate")
      || !cookiesLoaded.hasOwnProperty("graphs")
    ) {
      return defaultSettings;
    }

    return cookiesLoaded;
  } catch (err) {
    return defaultSettings;
  }
}

function initialDateRangeSelectors() {
  try {
    return JSON.parse(Cookies.get(DATE_RANGE_SELECTORS_KEY));
  } catch (err) {

    //DEFAULT SETTINGS
    const defaultSettings = {
      dataSource: 1,
      day: {
        human: "today",
        from: format(start_of_today(), "X"),
        to: format(end_of_today(), "X")
      },
      week: {
        human: "last7days",
        from: format(subDays(start_of_today(), 6), "X"),
        to: format(end_of_today(), "X")
      },
      year: {
        human: "thisYear",
        from: format(startOfYear(new Date()), "X"),
        to: format(end_of_today(), "X")
      }
    }

    return defaultSettings;
  }
}

function initialHierarchy() {
  try {
    return JSON.parse(Cookies.get(HIERRARCHY_KEY));
  } catch (err) {
    return {};
  }
}


function initialApps() {
  //DEFAULT DATA
  const defaultCookiesData = {
    controller: {
      map: {}
    }
  }

  try {
    const cookiesLoaded = JSON.parse(Cookies.get(APPLICATIONS_KEY));
    if (!cookiesLoaded.hasOwnProperty("controller")) {
      return defaultCookiesData;
    }

    return JSON.parse(Cookies.get(APPLICATIONS_KEY));
  } catch (err) {
    return defaultCookiesData;
  }
}

const cookiesStore = {
  namespaced: false,
  state: {
    dateRangeSelectors: initialDateRangeSelectors(),
    auth: initialAuth(),
    deviceList: initialDeviceList(),
    selectedDevice: initialDevice(),
    dateRange: initialDateRange(),
    settings: initialSettings(),
    hierarchy: initialHierarchy(),
    aplications: initialApps()
  },
  mutations: {
    setAuth(state, value) {
      //expires null mean end of relation
      Cookies.set(AUTH_KEY, JSON.stringify(value), { expires: null, secure: false, sameSite: 'strict' });
      state.auth = value;
    },

    setDateRange(state, payload) {
      Cookies.set(DATE_RANGE_KEY, JSON.stringify(payload), { expires: null, secure: false, sameSite: 'strict' });
      state.dateRange = payload;
    },

    setSelectedDevice(state, deviceSet) {
      const device = state.deviceList.filter(d => d.mac === deviceSet.mac)[0];
      if (device) {
        deviceSet.owner = device.owner;
        deviceSet.type = device.type;
        switch (device.type) {
          case 1:
            deviceSet.NILM = device.NILM.process;
            deviceSet.NILM_AUTOLEARN = device.NILM.autoLearn;
            deviceSet.phase_use = device.phase_use;
            deviceSet.lora = device.lora;
            break;
          case 3:
          case 4:
          case 5:
          case 6:
            deviceSet.phase_use = device.phase_use;
            break;
          case 12:
            deviceSet.NILM = device.NILM.process;
            deviceSet.NILM_AUTOLEARN = device.NILM.autoLearn;
            deviceSet.phase_use = device.phase_use;
            deviceSet.lora = device.lora;
            break;
        }
      }
      Cookies.set(SELECTED_DEVICE_KEY, JSON.stringify(deviceSet), { expires: EXPIRATION_GENERAL, secure: false, sameSite: 'strict' });
      state.selectedDevice = deviceSet;
    },

    setDeviceList(state, devices) {
      setDeviceList(devices);
      state.deviceList = devices;
    },

    setDeviceListStatus(state, list) {
      setDeviceList(list);
      state.deviceList = list;
    },

    removeDeviceFromDeviceList(state, deviceMAC) {
      state.deviceList = state.deviceList.filter(x => x.mac !== deviceMAC);
      setDeviceList(state.deviceList);
    },

    setSelectedPhase(state, payload) {
      state.settings.phase = payload;
      Cookies.set(SETTINGS_KEY, JSON.stringify(state.settings), { expires: EXPIRATION_GENERAL, secure: false, sameSite: 'strict' });
    },

    setLocation(state, payload) {
      state.settings.localization = payload;
      Cookies.set(SETTINGS_KEY, JSON.stringify(state.settings), { expires: EXPIRATION_GENERAL, secure: false, sameSite: 'strict' });
    },

    setSideBar(state, payload) {
      state.settings.sideBarMax = payload;
      Cookies.set(SETTINGS_KEY, JSON.stringify(state.settings), { expires: EXPIRATION_GENERAL, secure: false, sameSite: 'strict' });
    },

    setstatsDate(state, payload) {
      state.settings.statsDate = payload;
      Cookies.set(SETTINGS_KEY, JSON.stringify(state.settings), { expires: EXPIRATION_GENERAL, secure: false, sameSite: 'strict' });
    },

    setSelectedGraphs(state, payload) {
      state.settings.graphs = payload;
      Cookies.set(SETTINGS_KEY, JSON.stringify(state.settings), { expires: EXPIRATION_GENERAL, secure: false, sameSite: 'strict' });
    },

    setyAxisType(state, payload) {
      state.settings.yAxisType = payload;
      Cookies.set(SETTINGS_KEY, JSON.stringify(state.settings), { expires: EXPIRATION_GENERAL, secure: false, sameSite: 'strict' });
    },

    setDayDateRange(state, payload) {
      state.dateRangeSelectors.day = payload;
      Cookies.set(DATE_RANGE_SELECTORS_KEY, JSON.stringify(state.dateRangeSelectors), { expires: null, secure: false, sameSite: 'strict' });
    },

    setWeekDateRange(state, payload) {
      state.dateRangeSelectors.week = payload;
      Cookies.set(DATE_RANGE_SELECTORS_KEY, JSON.stringify(state.dateRangeSelectors), { expires: null, secure: false, sameSite: 'strict' });
    },

    setYearDateRange(state, payload) {
      state.dateRangeSelectors.year = payload;
      Cookies.set(DATE_RANGE_SELECTORS_KEY, JSON.stringify(state.dateRangeSelectors), { expires: null, secure: false, sameSite: 'strict' });
    },

    setDataSource(state, payload) {
      state.dateRangeSelectors.dataSource = payload;
      Cookies.set(DATE_RANGE_SELECTORS_KEY, JSON.stringify(state.dateRangeSelectors), { expires: null, secure: false, sameSite: 'strict' });
    },

    setHierarchy(state, value) {
      Cookies.set(HIERRARCHY_KEY, JSON.stringify(value), { expires: null, secure: false, sameSite: 'strict' });
      state.hierarchy = value;
    },

    setDateRange(state, payload) {
      Cookies.set(DATE_RANGE_KEY, JSON.stringify(payload), { expires: null, secure: false, sameSite: 'strict' });
      state.dateRange = payload;
    },

    setAPPController(state, value) {
      state.aplications.controller = value;
      Cookies.set(APPLICATIONS_KEY, JSON.stringify(state.aplications), { expires: null, secure: false, sameSite: 'strict' });
    },

    Logout(state) {
      Cookies.remove(HIERRARCHY_KEY);
      state.hierarchy = null;

      Cookies.remove(AUTH_KEY);
      state.auth = null;

      clearDeviceList();
      state.deviceList = null;
      
      clearUserToken();
      clearTokenRenewal();
    }
  },
  getters: {
    getAuth(state) {
      return state.auth;
    },
    getDateRange(state) {
      return state.dateRangeSelectors.day;
    },
    getSelectedDevice(state) {
      return state.selectedDevice;
    },
    getDeviceList(state) {
      return state.deviceList;
    },
    getSettings(state) {
      return state.settings;
    },
    getHierarchy(state) {
      return state.hierarchy;
    },
    getDateRangeSelectors(state) {
      return state.dateRangeSelectors;
    },
    getAPPController(state) {
      return state.aplications.controller;
    }
  },
}

const wsStore = {
  namespaced: false,
  state: {
    connected: false,
    message: "",
    notifications: []
  },
  mutations: {
    setConnected(state, value) {
      state.connected = value;
    },
    setDataMessage(state, msg) {
      state.message = msg
    },
    setNotificationList(state, msg) {
      if (state.notifications.length >= 5) {
        state.notifications.pop();
      }
      state.notifications.unshift(msg);
    },
  },
  getters: {
    isConnected(state) {
      return state.connected;
    },
    getDataMessage(state) {
      if (state.message) {
        try {
          return state.message.data;
        } catch (err) {
          console.log("Unable parse");
          return "";
        }
      }
    },
    getPayload(state) {
      if (state.message) {
        try {
          return state.message;
        } catch (err) {
          console.log("Unable parse");
          return "";
        }
      }
    },
    getNotificationList(state) {
      if (state.notifications) {
        try {
          return state.notifications;
        } catch (err) {
          return [];
        }
      }
    }
  },
  actions: {
    sendMessage: function (context, message) {
      Vue.prototype.$socket.send(message)
    }
  }
}

const updateStore = {
  namespaced: false,
  state: {
    overviewStats: false,
    showLoader: false
  },
  mutations: {
    setOverviewStats(state, value) {
      state.overviewStats = value;
    },
    setLoading(state, value) {
      state.showLoader = value;
    }
  },
  getters: {
    getOverviewStats(state) {
      return state.overviewStats;
    },
    getLoading(state) {
      return state.showLoader;
    },
  }
};

const store = new Vuex.Store({
  modules: {
    cookies: cookiesStore,
    ws: wsStore,
    update: updateStore
  }
});


export default store
